// umlaut-check: skip — Feldnamen ("mindestbestand", "saison_gepflegt" usw.)
// sind technische Bezeichner aus cosmos_artikel_parameter.

import React, {useState} from "react";
import {Button, Card, Col, OverlayTrigger, Row, Tooltip} from "react-bootstrap";
import axios from "axios";
import {FlexToast, useExternalControls} from "laravel-inertia-backend";
import FeldMitHilfe from "./FeldMitHilfe.jsx";
import HeaderWithHelp from "../SalesAnalysis/HeaderWithHelp.jsx";
import SelectList from "../SelectList.jsx";
import SicherheitAbzeichen from "./SicherheitAbzeichen.jsx";
import {formatMenge} from "./format.js";
import {TOOLTIP_POPPER_CONFIG} from "./tooltipConfig.js";

const GAENGIGKEIT_OPTIONEN = [
    {value: "", label: "– keine Angabe –"},
    {value: "Renner", label: "Renner"},
    {value: "Mittel", label: "Mittel"},
    {value: "Penner", label: "Penner"},
];

const MONATE = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

function herkunftFuer(herkunft, feld) {
    return herkunft && herkunft[feld] ? herkunft[feld] : null;
}

/**
 * Anzeigefeld der Parameter-Karte (Sichtprüfung 1, Befund 22): ein nacktes
 * "errechnet" ohne Zahl beantwortete nicht, WELCHER Wert gerade gilt. Gepflegte
 * Werte zeigen sich selbst; ungepflegte Felder mit einem errechneten Rückfall
 * (Mindestbestand/Zielbestand/Wiederbeschaffung, aus der Prognose) zeigen
 * "nicht gepflegt - errechnet: <Wert>"; Felder ganz ohne Rückfall (Palette,
 * Lagerkapazität, Gängigkeit, Sortierung, Bemerkung) zeigen schlicht
 * "nicht gepflegt".
 */
function AnzeigeFeld({label, wert, herkunft, suffix = "", errechnet, hilfe}) {
    const hatWert = wert !== null && wert !== undefined && wert !== "";
    const errechnetWert = errechnet && errechnet.wert !== null && errechnet.wert !== undefined ? errechnet.wert : null;

    let text;
    // Von Hand gepflegte Werte sind per Definition "gepflegt"; der
    // errechnete Rückfall trägt seine eigene Einstufung aus der Prognose
    // (meist "errechnet", teils "angenommen" - z. B. Wiederbeschaffungszeit
    // ohne gepflegten Wert, siehe Prognose.php). Auftrag Punkt 10: diese
    // Herkunft muss an der Kennzahl selbst sichtbar sein, nicht nur im Tooltip.
    let sicherheit = null;
    if (hatWert) {
        text = `${wert}${suffix}`;
        sicherheit = "gepflegt";
    } else if (errechnetWert !== null) {
        text = `nicht gepflegt – errechnet: ${formatMenge(errechnetWert)}${suffix}`;
        sicherheit = errechnet.sicherheit || "errechnet";
    } else {
        text = "nicht gepflegt";
    }

    const tooltipText = hatWert ? herkunft : errechnetWert !== null ? errechnet.quelle : null;
    const inhalt = (
        <span className={hatWert ? "fw-semibold" : "fw-semibold text-muted"}>
            {text}
            <SicherheitAbzeichen sicherheit={sicherheit} className="ms-1" />
        </span>
    );

    return (
        <div className="mb-2">
            <div className="small text-muted">
                {hilfe ? <HeaderWithHelp help={hilfe}>{label}</HeaderWithHelp> : label}
            </div>
            {tooltipText ? (
                <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`param-herkunft-${label}`}>{tooltipText}</Tooltip>}>
                    <div>{inhalt}</div>
                </OverlayTrigger>
            ) : (
                <div>{inhalt}</div>
            )}
        </div>
    );
}

function leeresFormular(artikel) {
    return {
        mindestbestand: artikel.mindestbestand ?? "",
        zielbestand: artikel.zielbestand ?? "",
        palette_stueck: artikel.palette_stueck ?? "",
        lagerkapazitaet_stueck: artikel.lagerkapazitaet_stueck ?? "",
        gaengigkeit: artikel.gaengigkeit ?? "",
        sortierung_hand: artikel.sortierung_hand ?? "",
        wiederbeschaffung_tage: artikel.wiederbeschaffung_tage ?? "",
        bemerkung: artikel.bemerkung ?? "",
        saison_gepflegt: artikel.saison_gepflegt
            ? MONATE.map((_, i) => artikel.saison_gepflegt[i + 1] ?? artikel.saison_gepflegt[String(i + 1)] ?? "")
            : null,
    };
}

/**
 * Parameter-Karte des Artikel-Blatts (AP8, UMSETZUNG.md C7): Anzeige immer,
 * Bearbeiten nur mit "cosmos.parameters". Speichert per axios ohne
 * Seiten-Neuladen und reicht die frisch gerechnete Prognose an die
 * aufrufende Seite zurück, damit der Zeitstrahl sich sofort aktualisiert.
 */
export default function ArtikelParameterKarte({artikel, artikelnummer, prognose, canParameters, onGespeichert}) {
    const {addToast} = useExternalControls();
    const [bearbeiten, setBearbeiten] = useState(false);
    const [formular, setFormular] = useState(() => leeresFormular(artikel));
    const [speichertGerade, setSpeichertGerade] = useState(false);

    const beginneBearbeiten = () => {
        setFormular(leeresFormular(artikel));
        setBearbeiten(true);
    };

    const feldAendern = (feld, wert) => setFormular((f) => ({...f, [feld]: wert}));

    const saisonAendern = (index, wert) => {
        setFormular((f) => {
            const basis = f.saison_gepflegt ? [...f.saison_gepflegt] : MONATE.map(() => "");
            basis[index] = wert;
            return {...f, saison_gepflegt: basis};
        });
    };

    const saisonZuruecksetzen = () => feldAendern("saison_gepflegt", null);

    const alsZahlOderNull = (wert) => (wert === "" || wert === null || wert === undefined ? null : Number(wert));

    const speichern = () => {
        setSpeichertGerade(true);
        const payload = {
            mindestbestand: alsZahlOderNull(formular.mindestbestand),
            zielbestand: alsZahlOderNull(formular.zielbestand),
            palette_stueck: alsZahlOderNull(formular.palette_stueck),
            lagerkapazitaet_stueck: alsZahlOderNull(formular.lagerkapazitaet_stueck),
            gaengigkeit: formular.gaengigkeit || null,
            sortierung_hand: alsZahlOderNull(formular.sortierung_hand),
            wiederbeschaffung_tage: alsZahlOderNull(formular.wiederbeschaffung_tage),
            bemerkung: formular.bemerkung || null,
            saison_gepflegt: formular.saison_gepflegt
                ? formular.saison_gepflegt.map((w) => (w === "" || w === null ? 1 : Number(w)))
                : null,
        };

        axios
            .put(`/cosmos/daten/artikel/${artikelnummer}/parameter`, payload)
            .then(({data}) => {
                setBearbeiten(false);
                onGespeichert?.(data);
                addToast(<FlexToast variant="success" title="Artikel-Parameter" message="Gespeichert." />);
            })
            .catch((e) => {
                addToast(
                    <FlexToast
                        variant="danger"
                        title="Artikel-Parameter"
                        autohide={false}
                        message={e.response?.data?.message || "Speichern fehlgeschlagen."}
                    />
                );
            })
            .finally(() => setSpeichertGerade(false));
    };

    return (
        <Card className="shadow-sm mb-3">
            <Card.Header className="d-flex align-items-center justify-content-between">
                <span className="fw-semibold">Parameter</span>
                {canParameters && !bearbeiten && (
                    <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="left" overlay={<Tooltip id="param-bearbeiten">Öffnet die Felder dieser Karte zum Bearbeiten.</Tooltip>}>
                        <span>
                            <Button size="sm" variant="outline-secondary" onClick={beginneBearbeiten}>
                                Bearbeiten
                            </Button>
                        </span>
                    </OverlayTrigger>
                )}
                {bearbeiten && (
                    <div className="d-flex gap-2">
                        <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id="param-abbrechen">Verwirft alle Änderungen an dieser Karte, ohne zu speichern.</Tooltip>}>
                            <span>
                                <Button size="sm" variant="outline-secondary" disabled={speichertGerade} onClick={() => setBearbeiten(false)}>
                                    Abbrechen
                                </Button>
                            </span>
                        </OverlayTrigger>
                        <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id="param-speichern">Speichert die Parameter dieses Artikels und berechnet die Prognose neu.</Tooltip>}>
                            <span>
                                <Button size="sm" variant="primary" disabled={speichertGerade} onClick={speichern}>
                                    {speichertGerade ? "speichert…" : "Speichern"}
                                </Button>
                            </span>
                        </OverlayTrigger>
                    </div>
                )}
            </Card.Header>
            <Card.Body>
                {!bearbeiten ? (
                    <Row className="g-3">
                        <Col xs={6} md={3}>
                            <AnzeigeFeld
                                label="Mindestbestand"
                                wert={artikel.mindestbestand !== null ? formatMenge(artikel.mindestbestand) : null}
                                herkunft={herkunftFuer(artikel.herkunft, "mindestbestand")}
                                suffix=" Stück"
                                errechnet={prognose?.mindestbestand}
                                hilfe="Sicherheitsbestand. Leer = errechnet aus der Einstellung „Mindestbestand“ (in Wochen) mal Wochenbedarf."
                            />
                        </Col>
                        <Col xs={6} md={3}>
                            <AnzeigeFeld
                                label="Zielbestand"
                                wert={artikel.zielbestand !== null ? formatMenge(artikel.zielbestand) : null}
                                herkunft={herkunftFuer(artikel.herkunft, "zielbestand")}
                                suffix=" Stück"
                                errechnet={prognose?.zielbestand}
                                hilfe="Wunsch-Vollstand. Leer = errechnet aus Lagerkapazität oder Mindestbestand plus Bestellmengen-Wochen."
                            />
                        </Col>
                        <Col xs={6} md={3}>
                            <AnzeigeFeld label="Palettengröße" wert={artikel.palette_stueck !== null ? formatMenge(artikel.palette_stueck) : null} herkunft={herkunftFuer(artikel.herkunft, "palette_stueck")} suffix=" Stück" hilfe="Stück je Palette – Bestellmenge wird darauf aufgerundet." />
                        </Col>
                        <Col xs={6} md={3}>
                            <AnzeigeFeld label="Lagerkapazität" wert={artikel.lagerkapazitaet_stueck !== null ? formatMenge(artikel.lagerkapazitaet_stueck) : null} herkunft={herkunftFuer(artikel.herkunft, "lagerkapazitaet_stueck")} suffix=" Stück" hilfe="Maximale Stückzahl im Lager (Oskars Datei) – Rückfall für den Zielbestand." />
                        </Col>
                        <Col xs={6} md={3}>
                            <AnzeigeFeld label="Gängigkeit" wert={artikel.gaengigkeit} herkunft={herkunftFuer(artikel.herkunft, "gaengigkeit")} hilfe="Oskars Einstufung: Renner, Mittel oder Penner." />
                        </Col>
                        <Col xs={6} md={3}>
                            <AnzeigeFeld
                                label="Wiederbeschaffung"
                                wert={artikel.wiederbeschaffung_tage}
                                herkunft={herkunftFuer(artikel.herkunft, "wiederbeschaffung_tage")}
                                suffix=" Tage"
                                errechnet={prognose?.wiederbeschaffung_tage}
                                hilfe="Von der Bestellung bis zum Wareneingang. Leer = die Einstellung „Wiederbeschaffungszeit“ auf der Seite „Quellen und Berechnungslogik“."
                            />
                        </Col>
                        <Col xs={6} md={3}>
                            <AnzeigeFeld label="Sortierung von Hand" wert={artikel.sortierung_hand} herkunft={herkunftFuer(artikel.herkunft, "sortierung_hand")} hilfe="Überschreibt die automatische Farbnummern-Reihenfolge in Listen." />
                        </Col>
                        <Col xs={12} md={9}>
                            <AnzeigeFeld label="Bemerkung" wert={artikel.bemerkung} herkunft={herkunftFuer(artikel.herkunft, "bemerkung")} hilfe="Freitext, z. B. Grund einer Sonderregel." />
                        </Col>
                    </Row>
                ) : (
                    <Row className="g-3">
                        <Col xs={6} md={3}>
                            <FeldMitHilfe id="p-mindestbestand" label="Mindestbestand" help="Sicherheitsbestand. Leer = errechnet aus der Einstellung „Mindestbestand“ (in Wochen) mal Wochenbedarf.">
                                <input id="p-mindestbestand" type="number" min="0" className="form-control form-control-sm" value={formular.mindestbestand} onChange={(e) => feldAendern("mindestbestand", e.target.value)} />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} md={3}>
                            <FeldMitHilfe id="p-zielbestand" label="Zielbestand" help="Wunsch-Vollstand. Leer = errechnet aus Lagerkapazität oder Mindestbestand + Bestellmengen-Wochen.">
                                <input id="p-zielbestand" type="number" min="0" className="form-control form-control-sm" value={formular.zielbestand} onChange={(e) => feldAendern("zielbestand", e.target.value)} />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} md={3}>
                            <FeldMitHilfe id="p-palette" label="Palettengröße" help="Stück je Palette – Bestellmenge wird darauf aufgerundet.">
                                <input id="p-palette" type="number" min="1" className="form-control form-control-sm" value={formular.palette_stueck} onChange={(e) => feldAendern("palette_stueck", e.target.value)} />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} md={3}>
                            <FeldMitHilfe id="p-lagerkapazitaet" label="Lagerkapazität" help="Maximale Stückzahl im Lager (Oskars Datei) – Rückfall für den Zielbestand.">
                                <input id="p-lagerkapazitaet" type="number" min="0" className="form-control form-control-sm" value={formular.lagerkapazitaet_stueck} onChange={(e) => feldAendern("lagerkapazitaet_stueck", e.target.value)} />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} md={3}>
                            <FeldMitHilfe id="p-gaengigkeit" label="Gängigkeit" help="Oskars Einstufung: Renner, Mittel oder Penner.">
                                <SelectList id="p-gaengigkeit" value={formular.gaengigkeit} options={GAENGIGKEIT_OPTIONEN} onChange={(v) => feldAendern("gaengigkeit", v)} size="sm" ariaLabel="Gängigkeit" />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} md={3}>
                            <FeldMitHilfe id="p-wiederbeschaffung" label="Wiederbeschaffung (Tage)" help="Von der Bestellung bis zum Wareneingang. Leer = die Einstellung „Wiederbeschaffungszeit“ auf der Seite „Quellen und Berechnungslogik“.">
                                <input id="p-wiederbeschaffung" type="number" min="0" className="form-control form-control-sm" value={formular.wiederbeschaffung_tage} onChange={(e) => feldAendern("wiederbeschaffung_tage", e.target.value)} />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} md={3}>
                            <FeldMitHilfe id="p-sortierung" label="Sortierung von Hand" help="Überschreibt die automatische Farbnummern-Reihenfolge in Listen.">
                                <input id="p-sortierung" type="number" className="form-control form-control-sm" value={formular.sortierung_hand} onChange={(e) => feldAendern("sortierung_hand", e.target.value)} />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={12}>
                            <FeldMitHilfe id="p-bemerkung" label="Bemerkung" help="Freitext, z. B. Grund einer Sonderregel.">
                                <textarea id="p-bemerkung" rows={2} className="form-control form-control-sm" value={formular.bemerkung} onChange={(e) => feldAendern("bemerkung", e.target.value)} />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={12}>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <FeldMitHilfe id="p-saison" label="Saisonfaktoren (gepflegt)" help="12 Monatsfaktoren von Hand, überschreiben den gemessenen Wert im Zeitstrahl. Leer lassen = errechnet." className="mb-0">
                                    <span />
                                </FeldMitHilfe>
                                <Button size="sm" variant="link" className="p-0" onClick={saisonZuruecksetzen}>
                                    Auf errechnet zurücksetzen
                                </Button>
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                                {MONATE.map((label, i) => (
                                    <div key={label} style={{width: 70}}>
                                        <div className="small text-muted text-center">{label}</div>
                                        <input
                                            type="number"
                                            step="0.05"
                                            min="0.1"
                                            max="5"
                                            className="form-control form-control-sm text-center"
                                            value={formular.saison_gepflegt ? formular.saison_gepflegt[i] : ""}
                                            placeholder="–"
                                            onChange={(e) => saisonAendern(i, e.target.value)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>
                )}
            </Card.Body>
        </Card>
    );
}
